import pluralize from "pluralize";
import { uuidv4 } from "../../Hooks/Display/utils";
import { icons } from "../Shared/IconPicker/IconPicker";
import Color from "../Shared/ColorPicker/ColorPalette";

export function transformPropertiesToAttributeFormat(attribute) {
    const properties = {
        attributeName: attribute.attributeName,
        description: attribute.description || attribute.attributeName,
        attributeLabel: attribute.attributeLabel || attribute.attributeName,
        sysName: attribute.attributeName,
        order: null,
        required: attribute.required || false,
        languages: {},
        unique: attribute.attributeType === "String" ? attribute.unique : false,
        readOnly: attribute.readOnly || false,
        tooltip: attribute.tooltip || attribute.attributeName,
        attributeType: attribute.attributeType,
        defaultValue: "",
        group: ""
    }

    switch (attribute.attributeType) {
        case "String":
            properties.min = attribute.min ? attribute.min : "";
            properties.max = attribute.max ? attribute.max : "";
            properties.pattern = attribute.pattern || "";
            properties.unique = attribute.unique || false;
            properties.dummyLabel = "";
            break;
        case "Number":
            properties.min = attribute.min || "";
            properties.max = attribute.max || "";
            properties.numberType = attribute.numberType || "integer";
            properties.useThousandSeparator = attribute.useThousandSeparator || false;
            properties.unitSide = attribute.unitSide || "Right";
            properties.unit = attribute.unit || "none";
            properties.decimalPlace = attribute.decimalPlace || "2";
            properties.attributeType = attribute.attributeType || "Number";
            properties.cellFormatting = JSON.stringify({ ifStatement: [], elseStatement: [] })
            break;
        case "Picklist String":
            properties.picklist = attribute.picklist ? attribute.picklist.join('ɖ') : "";
            properties.multiSelect = attribute.multiSelect || false;
            properties.cellFormatting = JSON.stringify({ ifStatement: [], elseStatement: [] })
            break;
        case "DateTime":
            properties.dateTimeType = attribute.dateTimeType || "datetime";
            properties.minTime = attribute.minTime || "";
            properties.maxTime = attribute.maxTime || "";
            properties.defaultTime = attribute.defaultTime || "12:00 AM";
            properties.interval = attribute.interval || "30";
            properties.defaultDate = attribute.defaultDate || "Today_+_0_Days";
            properties.minDate = attribute.minDate || "";
            properties.maxDate = attribute.maxDate || "";
            properties.setDefaultDate = attribute.setDefaultDate || false;
            properties.attributeType = "DateTime";
            properties.cellFormatting = JSON.stringify({ ifStatement: [], elseStatement: [] })
            break;
        case "Email":
            properties.dummyLabel = "Email"
            properties.unique = attribute.unique || false;
            break;
        case "Boolean":
            properties.defaultValue = attribute.defaultValue || false;
            properties.required = attribute.required || false;
            break;
        case "User Identity":
            properties.showProfilePicture = attribute.showProfilePicture || true;
            properties.useDisplayName = attribute.useDisplayName || true;
            properties.multiSelect = attribute.multiSelect || false;
            break;
        case "State Field":
            const states = attribute.statefield || [];
            properties.statefield = states.map(state => ({
                _id: uuidv4(),
                name: state.name,
                color: "rgba(255,255,255, 1)",
                createdDate: new Date(),
                updatedDate: new Date(),
            }));
            properties.setStatesRules = attribute.setStatesRules || false;
            properties.cellFormatting = JSON.stringify({ ifStatement: [], elseStatement: [] })
            properties.required = attribute.required || true;
            properties.rules = attribute.rules || [];
            break;
        case "Tree Dropdown":
            debugger;
            if (!attribute.treeDropdown) {
                throw new Error("Tree dropdown must be an array of items.");
            }
            if (attribute.treeDropdown && !Array.isArray(attribute.treeDropdown)) {
                throw new Error("Tree dropdown must be an array of items.");
            }
            const numberOfRoots = attribute.treeDropdown.filter(item => !item.parentName).length;
            if (numberOfRoots !== 1) {
                throw new Error("Tree dropdown must have exactly one root item.");
            }
            const treeDropdown = convertItemsToTree(attribute.treeDropdown);
            properties.treeDropdown = treeDropdown;
            break;
        case "Reference Attribute":
        case "Mirror":
            if (!attribute.referenceEntityId) {
                throw new Error("Referenced entity ID is required for reference attribute.");
            }
            if (!attribute.referenceAttributeId) {
                throw new Error("Referenced attribute ID is required for reference attribute.");
            }
            if (!attribute.referenceRelationId) {
                throw new Error("Reference relation ID is required for reference attribute.");
            }
            if (properties.attributeType === "Reference Attribute") {
                properties.attributeType = "Entity Lookup"; 
            }
            properties.references = {
                entityId: attribute.referenceEntityId,
                attributeId: attribute.referenceAttributeId,
                linkedEntityId: attribute.referenceRelationId
            }
        default:
            break;
    }

    if (attribute.attributeType !== "String") {
        delete properties.unique;
    }

    return properties;
}

function convertItemsToTree(items) {
    if (!Array.isArray(items)) {
        throw new Error("Input must be an array of items.");
    }

    // Map to store nodes keyed by name for fast lookup.
    const nodeMap = new Map();

    let numItemsWithoutParent = 0;
    // First pass: validate items and create a node for each.
    items.forEach(item => {
        debugger;
        if (!item.name || typeof item.name !== "string" || item.name.trim() === "") {
            throw new Error("Each item must have a non-empty 'name'.");
        }
        const shouldThrowParentError = numItemsWithoutParent > 1;
        if (!item.parentName) {
            numItemsWithoutParent++;
        }
        if (shouldThrowParentError && (!item.parentName || typeof item.parentName !== "string" || item.parentName.trim() === "")) {
            throw new Error(`Item '${item.name}' must have a 'parentName'.`);
        }
        const trimmedName = item.name.trim();
        if (nodeMap.has(trimmedName)) {
            throw new Error(`Duplicate item name found: '${trimmedName}'.`);
        }
        // Create a new node with required properties.
        const node = {
            id: uuidv4(),
            text: trimmedName,
            parentId: null,      // Will be set in second pass.
            items: [],
            expandedLevel: 0,    // Will be computed later.
            isFirst: false,      // Will be set later.
            isLast: false,       // Will be set later.
            root: false          // Will be set later.
        };
        // Temporarily store the parent's name (if any) for later linking.
        node._tempParentName = (item.parentName && item.parentName.trim()) || null;
        nodeMap.set(trimmedName, node);
    });

    // Second pass: build the tree structure by linking nodes.
    const roots = [];
    nodeMap.forEach(node => {
        const parentName = node._tempParentName;
        if (parentName) {
            if (!nodeMap.has(parentName)) {
                throw new Error(`Parent name '${parentName}' not found for item '${node.text}'.`);
            }
            const parentNode = nodeMap.get(parentName);
            node.parentId = parentNode.id;
            parentNode.items.push(node);
        } else {
            // No parent: this is a root node.
            node.parentId = null;
            roots.push(node);
        }
        // Remove the temporary property.
        delete node._tempParentName;
    });

    // Recursive function to set expandedLevel and the sibling flags.
    function setLevelsAndSiblings(nodes, level) {
        nodes.forEach((node, index) => {
            node.expandedLevel = level;
            node.isFirst = (index === 0);
            node.isLast = (index === nodes.length - 1);
            // Recurse for child nodes.
            if (node.items && node.items.length > 0) {
                setLevelsAndSiblings(node.items, level + 1);
            }
        });
    }

    // Mark root nodes and set levels.
    roots.forEach(node => { node.root = true; });
    setLevelsAndSiblings(roots, 1);

    return roots;
}

export function transformPropertiesToAttributeFormatForUpdate(projectId, attribute, existingProperties = {}) {
    const properties = {
        attributeName: attribute.attributeName,
        description: attribute.description || attribute.attributeName,
        attributeLabel: attribute.attributeLabel || attribute.attributeName,
        sysName: attribute.attributeName,
        order: existingProperties.order ?? null,
        required: attribute.required ?? existingProperties.required ?? false,
        languages: existingProperties.languages ?? {},
        unique: attribute.attributeType === "String" ? attribute.unique ?? existingProperties.unique ?? false : undefined,
        readOnly: attribute.readOnly ?? existingProperties.readOnly ?? false,
        tooltip: attribute.tooltip || attribute.attributeName,
        attributeType: attribute.attributeType,
        defaultValue: attribute.defaultValue ?? existingProperties.defaultValue ?? "",
        group: existingProperties.group ?? "",
        projectId: projectId,
        isLabelChanged: attribute.attributeLabel && attribute.attributeLabel !== existingProperties.attributeLabel,
        isDescriptionChanged: attribute.description && attribute.description !== existingProperties.description,
        isNameChanged: attribute.attributeName && attribute.attributeName !== existingProperties.attributeName,
        entityId: existingProperties.entityId,
        isSystemAttribute: existingProperties.isSystemAttribute ?? undefined,
        isAzureAttribute: existingProperties.isAzureAttribute ?? undefined,
    };

    switch (attribute.attributeType) {
        case "String":
            properties.min = attribute.min ?? existingProperties.min ?? "";
            properties.max = attribute.max ?? existingProperties.max ?? "";
            properties.pattern = attribute.pattern ?? existingProperties.pattern ?? "";
            properties.unique = attribute.unique ?? existingProperties.unique ?? false;
            properties.dummyLabel = existingProperties.dummyLabel ?? "";
            break;
        case "Number":
            properties.min = attribute.min ?? existingProperties.min ?? "";
            properties.max = attribute.max ?? existingProperties.max ?? "";
            properties.numberType = attribute.numberType ?? existingProperties.numberType ?? "integer";
            properties.useThousandSeparator = attribute.useThousandSeparator ?? existingProperties.useThousandSeparator ?? false;
            properties.unitSide = attribute.unitSide ?? existingProperties.unitSide ?? "Right";
            properties.unit = attribute.unit ?? existingProperties.unit ?? "none";
            properties.decimalPlace = attribute.decimalPlace ?? existingProperties.decimalPlace ?? "2";
            properties.cellFormatting = JSON.stringify({ ifStatement: [], elseStatement: [] });
            break;
        case "Picklist String":
            properties.picklist = attribute.picklist ? attribute.picklist.join('ɖ') : existingProperties.picklist ?? "";
            properties.multiSelect = attribute.multiSelect ?? existingProperties.multiSelect ?? false;
            properties.cellFormatting = JSON.stringify({ ifStatement: [], elseStatement: [] });
            break;
        case "DateTime":
            properties.dateTimeType = attribute.dateTimeType ?? existingProperties.dateTimeType ?? "datetime";
            properties.minTime = attribute.minTime ?? existingProperties.minTime ?? "";
            properties.maxTime = attribute.maxTime ?? existingProperties.maxTime ?? "";
            properties.defaultTime = attribute.defaultTime ?? existingProperties.defaultTime ?? "12:00 AM";
            properties.interval = attribute.interval ?? existingProperties.interval ?? "30";
            properties.defaultDate = attribute.defaultDate ?? existingProperties.defaultDate ?? "Today_+_0_Days";
            properties.minDate = attribute.minDate ?? existingProperties.minDate ?? "";
            properties.maxDate = attribute.maxDate ?? existingProperties.maxDate ?? "";
            properties.setDefaultDate = attribute.setDefaultDate ?? existingProperties.setDefaultDate ?? false;
            properties.cellFormatting = JSON.stringify({ ifStatement: [], elseStatement: [] });
            break;
        case "Email":
            properties.dummyLabel = "Email";
            properties.unique = attribute.unique ?? existingProperties.unique ?? false;
            break;
        case "Boolean":
            properties.defaultValue = attribute.defaultValue ?? existingProperties.defaultValue ?? false;
            properties.required = attribute.required ?? existingProperties.required ?? false;
            break;
        case "User Identity":
            properties.showProfilePicture = attribute.showProfilePicture ?? existingProperties.showProfilePicture ?? true;
            properties.useDisplayName = attribute.useDisplayName ?? existingProperties.useDisplayName ?? true;
            properties.multiSelect = attribute.multiSelect ?? existingProperties.multiSelect ?? false;
            break;
        case "State Field":
            if (attribute.statefield && attribute.statefield.length > 0) {
                const newStates = attribute.statefield.map(state => {
                    const prevState = existingProperties.statefield.find(prevState => prevState.name === state.name);
                    if (prevState) {
                        return prevState
                    }
                    return {
                        _id: uuidv4(),
                        name: state.name,
                        color: state.color,
                        createdDate: new Date(),
                        updatedDate: new Date(),
                    }
                });
                properties.statefield = newStates;
            } else {
                properties.statefield = existingProperties.statefield ?? [];
            }
            properties.setStatesRules = attribute.setStatesRules ?? existingProperties.setStatesRules ?? false;
            properties.cellFormatting = JSON.stringify({ ifStatement: [], elseStatement: [] });
            properties.required = attribute.required ?? existingProperties.required ?? true;
            if (attribute.rules) {
                for (const rule of attribute.rules) {
                    if (rule.fromStateId === rule.toStateId) {
                        throw new Error("From state and to state cannot be same");
                    }
                    if (rule.fromStateId === "" || rule.toStateId === "") {
                        throw new Error("Please select from and to state for the rule");
                    }
                    const fromState = properties.statefield.find(state => state._id === rule.fromStateId);
                    const toState = properties.statefield.find(state => state._id === rule.toStateId);
                    if (!fromState || !toState) {
                        throw new Error("From or to state not found");
                    }
                    if (rule.roleIds.length === 0) {
                        throw new Error("Please select atleast one role for the rule");
                    }
                }
                properties.rules = attribute.rules;
            } else {
                properties.rules = existingProperties.rules ?? [];
            }
            break;
        default:
            break;
    }

    if (attribute.attributeType !== "String") {
        delete properties.unique;
    }

    return properties;
}

export function getRandomIcon() {
    return icons[Math.floor(Math.random() * icons.length) % icons.length];
}

export function transformToCreateEntityDatabaseFormat(properties) {
    return {
        name: properties.name,
        description: properties.description || properties.name,
        label: properties.label || properties.name,
        pluralLabel: properties.pluralLabel || pluralize(properties.name),
        sysName: properties.name,
        order: properties.order,
        Icon: properties.icon || getRandomIcon(),
        IconColor: "#000",
        allowAttachement: false,
        allowMultiple: true,
        allowSize: "5",
        allowTypes: ".doc,.jpg,.pdf,.zip,.json",
        languages: {},
        configuration: {
            useInPortal: true,
            isConfigured: true,
            shouldCreateTable: true,
            shouldCreateCalendar: true,
            shouldCreateKanban: true,
            shouldCreateCards: true,
            shouldCreateSplitView: true,
            shouldCreateDetailsView: true,
            shouldCreateNewRecord: true,
            shouldCreateEditRecord: true,
            shouldCreateReference: false,
            shouldCreateLinkRecordDetails: false,
            shouldCreateLinkRecord: false,
            shouldCreateGanttChart: true,
            previousUseInPortalValue: true,
            detailsViewStyle: "Layout 1"
        }
    }
}

export function transformToPortalFormat(properties) {
    return {
        portalName: properties.portalName.trim().replace(/\s\s+/g, " "),
        portalDescription: properties.description,
        Icon: getRandomIcon(),
        IconColor: Color(),
        urlName: properties.portalName.replace(/[^a-zA-Z0-9-._~]/g, ""),
        isActive: true,
        logoUrl: "",
        faviconUrl: "",
        enableDashboard: true,
        enableReporting: true,
        isWorkflowEnabled: true,
        order: 0,
        navigationBarOrientation: "Top",
        navigateToDefaultPage: "false",
        entityId: "",
        targetPageId: "",
    }
}

export function transformPropertiesToRoleFormat(roleName) {
    return {
        roleName: roleName,
        manageUsers: false,
        provideFeedback: true,
        configureApp: false,
        appGeneralSettings: false,
        viewAppasOtherRolesinPublish: false,
        lookupRights: "Access",
        dashboardingRights: "Access",
        reportingRights: "Access",
        viewAppasOtherRoles: false,
        configureAppinPreview: false,
        importRights: false,
        dataAccessRights: false,
        importRightsinPreview: false,
        notificationRights: "Access",
        sharedFormRights: "No access",
        apiRights: false,
        allowTaskWorkflowManagement: "Access",
        internalWorkflowRights: "Access",
    }
}

export function getComponentById(components, id) {
    for (const component of components) {
        if (component.id === id) {
            return component;
        }
        if (component.children) {
            const comp = getComponentById(component.children, id);
            if (comp) {
                return comp;
            }
        }
    }
    return null;
}

export function getComponentsByType(components, type) {
    const result = [];
    for (const component of components) {
        if (component.componentType === type) {
            result.push(component);
        }
        if (component.children) {
            result.push(...getComponentsByType(component.children, type));
        }
    }
    return result;
}

export function getActionById(actions, id) {
    for (const action of actions) {
        if (action.id === id) {
            return action;
        }
        if (action.actions) {
            const act = getActionById(action.actions, id);
            if (act) {
                return act;
            }
        }
    }
    return null;
}

export function getActionParentById(actions, id) {
    for (const action of actions) {
        if (action.actions) {
            if (action.actions.find(act => act.id === id)) {
                return action;
            }
        }
    }
    return null;
}

export function extractPropertiesFromTree(tree, properties) {
    const extracted = [];
    for (const node of tree) {
        const json = {};
        for (const property of properties) {
            if (node[property]) {
                json[property] = node[property];
            }
        }
        if (node.children) {
            json.children = extractPropertiesFromTree(node.children, properties);
        }
        extracted.push(json);
    }
    return extracted;
}

export function getNestedValue(tree, childrenKey, key, value) {
    for (const node of tree) {
        if (node[key] === value) {
            return node;
        }
        if (node[childrenKey]) {
            const result = getNestedValue(node[childrenKey], childrenKey, key, value);
            if (result) {
                return result;
            }
        }
    }
    return null;
}

export function requestInformationFromAppModel(organizationId, projectId, instanceId) {
    if (window.parent !== window) {
        debugger;
        const iframes = window.parent;
        const numIframes = iframes.length;
        let realtimeApiIframe = null;            
        const realtimeIframePathname = `/_workspacesettings/appmodel/${organizationId}/projects/${projectId}`;
        for (let i = 0; i < numIframes; i++) {
            if (iframes[i].location.pathname === realtimeIframePathname) {
                realtimeApiIframe = iframes[i];
                break;
            }
        }
        if (realtimeApiIframe) {
            const type = "GET_CONTEXT_REALTIME_API";
            realtimeApiIframe.postMessage(JSON.stringify({ type, instanceId }), window.location.origin);
        }
    }
}